import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import LayoutSidebar from '../../../components/LayoutSidebar'
import { selectAuth } from '../../../redux/authSlice'
import { selectPortfolios } from "../../../redux/portfolioSlice"
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { selectUser } from '../../../redux/userSlice'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import Loading from '../../../components/Loading'
import DeclineIcon from '../../../components/elements/DeclineIcon'
import SuccessIcon from '../../../components/elements/SuccessIcon'

export default function InquirySearch() {
  const request = useRequest()
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth);
  const [responseData, setResponseData] = useState([])
  const [validPortfolios, setValidPortfolios] = useState([]);
  const portfolios = useSelector(selectPortfolios)
  const active = useSelector(selectUser)
  const { handleSubmit, control, watch, setError, clearErrors, reset, setValue, formState: { errors } } = useForm();
  const [searching, setSearching] = useState(false)
  const [leadProviders, setLeadProviders] = useState([])
  const inquiryDateStart = watch("inquiry_date_start");

  useEffect(() => {
    if (active.data.limit_to_portfolio_id || active?.spoofed_user?.limit_to_portfolio_id) {
      let limitedPortfolioIds = active?.spoofed_user?.limit_to_portfolio_id ?? (active.data.limit_to_portfolio_id);
      let limitedPortfolio = []
      limitedPortfolioIds.forEach((portfolioId) => {
        let portfolioData = portfolios.data.filter((portfolio) => portfolio.id === portfolioId);
        portfolioData = portfolioData[0] ?? null
        if (portfolioData) {
          limitedPortfolio.push(portfolioData)
        }
      })
      setValidPortfolios(limitedPortfolio)
    } else {
      setValidPortfolios(portfolios.data);
    }
  }, [active, portfolios])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      fetchLeadProviders()
    }
  }, [auth.accessToken])

  const fetchLeadProviders = async () => {
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/report/inquiry-search/lead-providers`)
      const response = await request.get()
      setLeadProviders(response?.data?.data)
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const onSubmit = async (formData) => {
    setSearching(true)
    setResponseData([])
    try {
      formData.portfolio_id = formData.portfolio_id?.value;
      formData.lead_provider_id = formData.lead_provider_id?.value;
      // Filter out undefined values
      const filteredData = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value !== undefined && value !== "")
      );

      // This form has an SSN field, so it uses POST instead of GET to keep the value out
      // of the URL. useRequest's post() doesn't append spoofUserID like get() does, so
      // it's added manually here: the backend reads it from the query string, not the
      // JSON body.
      const spoofParam = active.spoofed_user?.id ? `?spoofUserID=${active.spoofed_user.id}` : ''
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/report/inquiry-search${spoofParam}`)
      const response = await request.post(filteredData)
      setResponseData(response?.data?.data);
      setSearching(false)
    } catch (error) {
      setSearching(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const handleReset = () => {
    reset({
      inquiry_date_start: '',
      inquiry_date_end: '',
      ssn: '',
      last_name: '',
      first_name: '',
      portfolio_id: null,
      lead_provider_id: null
    });
    setValue('portfolio_id', null);
    setValue('lead_provider_id', null);
  };

  return (
    <LayoutSidebar
      contentTitle={'Inquiry Search'}
      content={(
        <div className="lox-detail mt-1 mx-1">
          <div className="columns mb-0">
            <div className="column is-one-quarter is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start">
              <form onSubmit={handleSubmit(onSubmit)} id="inquiry_search" className="lox-form">
                <div className="column">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Inquiry Date</span>
                  </p>
                  <Controller
                    name="inquiry_date_start"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input is-small"
                        type="date"
                        data-testid="inquiry-search-date-start"
                        value={field.value || ""}
                        onChange={(e) => {
                          field.onChange(e);
                          clearErrors("inquiry_date_end"); // Clear errors when start date changes
                        }}
                      />
                    )}
                  />
                </div>

                <div className="column">
                  <Controller
                    name="inquiry_date_end"
                    control={control}
                    defaultValue=""
                    rules={{
                      required: true,
                      validate: (value) => {
                        if (!inquiryDateStart) return true; // If start date is not set, skip validation
                        const startDate = new Date(inquiryDateStart);
                        const endDate = new Date(value);
                        const diffTime = Math.abs(endDate - startDate);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return diffDays <= 31 || "End date must be within 1 month of start date";
                      },
                    }}
                    render={({ field }) => (
                      <div>
                        <input
                          {...field}
                          className="input is-small"
                          type="date"
                          data-testid="inquiry-search-date-end"
                          value={field.value || ""}
                        />
                        {errors.inquiry_date_end && (
                          <p className="has-text-danger is-size-7">
                            {errors.inquiry_date_end.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="column">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">SSN (last 4)</span>
                  </p>
                  <Controller
                    name="ssn"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input is-small"
                        type="text"
                      />
                    )}
                  />
                </div>

                <div className="column">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Last Name</span>
                  </p>
                  <Controller
                    name="last_name"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input is-small"
                        type="text"
                      />
                    )}
                  />
                </div>

                <div className="column">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">First Name</span>
                  </p>
                  <Controller
                    name="first_name"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input is-small"
                        type="text"
                      />
                    )}
                  />
                </div>

                <div className="column">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Portfolio</span>
                  </p>
                  <div style={{ width: "100%" }}>
                    <Controller
                      name="portfolio_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isSearchable={false}
                          classNames={reactSelectClassNames()}
                          options={validPortfolios?.filter((o) => o.id != null).map((o) => ({ label: o.name, value: o.id }))}
                          onChange={(val) => {
                            field.onChange({ label: val?.label, value: val?.value })
                          }}
                          menuPosition="fixed"
                          styles={{ menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="column">
                  <p className="has-text-grey is-small is-size-7">
                    <span className="mr-1">Lead Provider</span>
                  </p>
                  <div style={{ width: "100%" }}>
                    <Controller
                      name="lead_provider_id"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          isSearchable={false}
                          classNames={reactSelectClassNames()}
                          options={leadProviders?.filter((o) => o.id != null).map((o) => ({ label: o.name, value: o.id }))}
                          onChange={(val) => {
                            field.onChange({ label: val?.label, value: val?.value })
                          }}
                          menuPosition="fixed"
                          styles={{ menu: ({ ...css }) => ({ ...css, width: '100%' }) }}
                          isClearable
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="column has-text-right">
                  <button type="button" className="button is-text is-small px-2 mr-2" onClick={() => handleReset()}>Clear all filters</button>
                  <button type="submit" className="button is-link is-small px-2" data-testid="inquiry-search-submit" disabled={searching && 'disabled'}>{searching ? 'Searcing' : 'Search'}</button>
                </div>

              </form>
            </div>

            <div className="column is-flex is-flex-direction-column mt-2 is-align-items-flex-center">
              <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-fullwidth is-hoverable mb-3" data-testid="inquiry-search-results-table">
                <thead>
                  <tr>
                    <th className="is-selected has-text-centered">Last Name</th>
                    <th className="is-selected has-text-centered">First Name</th>
                    <th className="is-selected has-text-centered">Last 4 SSN</th>
                    <th className="is-selected has-text-centered">Date of Birth</th>
                    <th className="is-selected has-text-centered">Inquiry Date</th>
                    <th className="is-selected has-text-centered">Portfolio</th>
                    <th className="is-selected has-text-centered">Lead Provider</th>
                    <th className="is-selected has-text-centered">Clarity</th>
                    <th className="is-selected has-text-centered">Factor Trust</th>
                  </tr>
                </thead>
                <tbody>
                  {responseData.map((item, index) => (
                    <tr key={item.lead_id}>
                      <td className="has-text-centered">{item.last_name}</td>
                      <td className="has-text-centered">{item.first_name}</td>
                      <td className="has-text-centered">{item.last_4_ssn}</td>
                      <td className="has-text-centered">{item.date_of_birth}</td>
                      <td className="has-text-centered">{item.inquiry_date}</td>
                      <td className="has-text-centered">{portfolios?.data.find((o) => o.id === item.portfolio_id)?.name}</td>
                      <td className="has-text-centered">{item.lead_provider}</td>

                      <td className="has-text-centered">{item.clarity_inquiry ? (<SuccessIcon />) : (<DeclineIcon />)}</td>
                      <td className="has-text-centered">{item.factor_trust_inquiry ? (<SuccessIcon />) : (<DeclineIcon />)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {searching && (
                <Loading />
              )}
            </div>
          </div>

        </div>
      )}
    />
  )
}
